/**
 * Page component per manifest route (lazy: one chunk per page). routes.test.ts checks this covers
 * every route in manifest.ts.
 */
import type { ComponentType, LazyExoticComponent } from 'react';
import { lazyPage } from './lazy-page';
import type { RouteId } from './manifest';

export const PAGES: Record<
  Exclude<RouteId, 'design-system'>,
  LazyExoticComponent<ComponentType>
> = {
  'auth.login': lazyPage(() => import('../pages/auth/AuthLoginPage')),
  'auth.signup': lazyPage(() => import('../pages/auth/AuthSignupPage')),
  'auth.lost-password': lazyPage(() => import('../pages/auth/AuthLostPasswordPage')),
  'auth.reset-password': lazyPage(() => import('../pages/auth/AuthResetPasswordPage')),
  'auth.activate': lazyPage(() => import('../pages/auth/AuthActivatePage')),
  'auth.logout': lazyPage(() => import('../pages/auth/AuthLogoutPage')),
  'status.maintenance': lazyPage(() => import('../pages/status/StatusMaintenancePage')),
  'status.error': lazyPage(() => import('../pages/status/StatusErrorPage')),
  home: lazyPage(() => import('../pages/home/HomePage')),
  'select-project': lazyPage(() => import('../pages/home/SelectProjectPage')),
  'issues.list': lazyPage(() => import('../pages/issues/IssuesListPage')),
  'issues.new': lazyPage(() => import('../pages/issues/IssuesNewPage')),
  'issues.filter': lazyPage(() => import('../pages/issues/IssuesFilterPage')),
  'issues.bulk': lazyPage(() => import('../pages/issues/IssuesBulkPage')),
  'issues.export': lazyPage(() => import('../pages/issues/IssuesExportPage')),
  'issues.print': lazyPage(() => import('../pages/issues/IssuesPrintPage')),
  'issues.view': lazyPage(() => import('../pages/issues/IssuesViewPage')),
  'issues.edit': lazyPage(() => import('../pages/issues/IssuesEditPage')),
  'issues.status': lazyPage(() => import('../pages/issues/IssuesStatusPage')),
  'issues.reminder': lazyPage(() => import('../pages/issues/IssuesReminderPage')),
  'issues.graph': lazyPage(() => import('../pages/issues/IssuesGraphPage')),
  'issues.revisions': lazyPage(() => import('../pages/issues/IssuesRevisionsPage')),
  'notes.edit': lazyPage(() => import('../pages/issues/NotesEditPage')),
  'notes.revisions': lazyPage(() => import('../pages/issues/NotesRevisionsPage')),
  'revisions.view': lazyPage(() => import('../pages/issues/RevisionsViewPage')),
  'files.download': lazyPage(() => import('../pages/issues/FilesDownloadPage')),
  'filters.list': lazyPage(() => import('../pages/filters/FiltersListPage')),
  'filters.new': lazyPage(() => import('../pages/filters/FiltersNewPage')),
  'filters.edit': lazyPage(() => import('../pages/filters/FiltersEditPage')),
  'filters.permalink': lazyPage(() => import('../pages/filters/FiltersPermalinkPage')),
  changelog: lazyPage(() => import('../pages/project/ChangelogPage')),
  roadmap: lazyPage(() => import('../pages/project/RoadmapPage')),
  summary: lazyPage(() => import('../pages/project/SummaryPage')),
  'summary.trends': lazyPage(() => import('../pages/project/SummaryTrendsPage')),
  'summary.graphs': lazyPage(() => import('../pages/project/SummaryGraphsPage')),
  documents: lazyPage(() => import('../pages/project/DocumentsPage')),
  'documents.new': lazyPage(() => import('../pages/project/DocumentsNewPage')),
  'documents.edit': lazyPage(() => import('../pages/project/DocumentsEditPage')),
  news: lazyPage(() => import('../pages/project/NewsPage')),
  'news.new': lazyPage(() => import('../pages/project/NewsNewPage')),
  'news.view': lazyPage(() => import('../pages/project/NewsViewPage')),
  'news.edit': lazyPage(() => import('../pages/project/NewsEditPage')),
  billing: lazyPage(() => import('../pages/project/BillingPage')),
  'tags.view': lazyPage(() => import('../pages/project/TagsViewPage')),
  'users.view': lazyPage(() => import('../pages/project/UsersViewPage')),
  account: lazyPage(() => import('../pages/account/AccountPage')),
  'account.preferences': lazyPage(() => import('../pages/account/AccountPreferencesPage')),
  'account.columns': lazyPage(() => import('../pages/account/AccountColumnsPage')),
  'account.profiles': lazyPage(() => import('../pages/account/AccountProfilesPage')),
  'account.profiles.edit': lazyPage(() => import('../pages/account/AccountProfilesEditPage')),
  'account.tokens': lazyPage(() => import('../pages/account/AccountTokensPage')),
  'account.sponsorships': lazyPage(() => import('../pages/account/AccountSponsorshipsPage')),
  manage: lazyPage(() => import('../pages/manage/ManagePage')),
  'manage.users': lazyPage(() => import('../pages/manage/ManageUsersPage')),
  'manage.users.new': lazyPage(() => import('../pages/manage/ManageUsersNewPage')),
  'manage.users.edit': lazyPage(() => import('../pages/manage/ManageUsersEditPage')),
  'manage.projects': lazyPage(() => import('../pages/manage/ManageProjectsPage')),
  'manage.projects.new': lazyPage(() => import('../pages/manage/ManageProjectsNewPage')),
  'manage.projects.edit': lazyPage(() => import('../pages/manage/ManageProjectsEditPage')),
  'manage.versions.edit': lazyPage(() => import('../pages/manage/ManageVersionsEditPage')),
  'manage.categories.edit': lazyPage(() => import('../pages/manage/ManageCategoriesEditPage')),
  'manage.custom-fields': lazyPage(() => import('../pages/manage/ManageCustomFieldsPage')),
  'manage.custom-fields.edit': lazyPage(() => import('../pages/manage/ManageCustomFieldsEditPage')),
  'manage.profiles': lazyPage(() => import('../pages/manage/ManageProfilesPage')),
  'manage.tags': lazyPage(() => import('../pages/manage/ManageTagsPage')),
  'manage.tags.edit': lazyPage(() => import('../pages/manage/ManageTagsEditPage')),
  'manage.plugins': lazyPage(() => import('../pages/manage/ManagePluginsPage')),
  'manage.plugins.formatting': lazyPage(
    () => import('../pages/manage/ManagePluginsFormattingPage'),
  ),
  'manage.plugins.import-export': lazyPage(
    () => import('../pages/manage/ManagePluginsImportExportPage'),
  ),
  'manage.import': lazyPage(() => import('../pages/manage/ManageImportPage')),
  'manage.config': lazyPage(() => import('../pages/manage/ManageConfigPage')),
  'manage.config.edit': lazyPage(() => import('../pages/manage/ManageConfigEditPage')),
  'manage.config.permissions': lazyPage(
    () => import('../pages/manage/ManageConfigPermissionsPage'),
  ),
  'manage.config.thresholds': lazyPage(() => import('../pages/manage/ManageConfigThresholdsPage')),
  'manage.config.workflow': lazyPage(() => import('../pages/manage/ManageConfigWorkflowPage')),
  'manage.config.workflow-graph': lazyPage(
    () => import('../pages/manage/ManageConfigWorkflowGraphPage'),
  ),
  'manage.config.email': lazyPage(() => import('../pages/manage/ManageConfigEmailPage')),
  'manage.config.columns': lazyPage(() => import('../pages/manage/ManageConfigColumnsPage')),
  'manage.system': lazyPage(() => import('../pages/manage/ManageSystemPage')),
};
